import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowUpRight, ArrowLeftRight, ShieldCheck, Zap, Layers, TrendingUp, TrendingDown,
  Monitor, Globe, ChevronRight, ChevronLeft, Star, Quote, Wallet,
  UserPlus, LogIn, Landmark, Bot, Headphones, Coins, LineChart,
  CreditCard, Bitcoin, Play, Calendar,
} from "lucide-react";
import api from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { useParallax } from "@/lib/parallax";
import { INSTRUMENTS, SPREADS, tick, quote } from "@/lib/market";
import { useLiveRates, mergeLive, anyLive } from "@/lib/liveRates";
import Sparkline from "@/components/public/Sparkline";
import MarketMotion from "@/components/public/MarketMotion";
import { PANEL_ICONS } from "@/lib/panelIcons";

const HERO_KEYS = ["EUR/USD", "BTC/USD", "ETH/USD", "AAPL", "XAU/USD"];
const FLAT_ALL = Object.values(INSTRUMENTS).flat();
const HERO_BASE = HERO_KEYS.map((k) => FLAT_ALL.find((r) => r.s === k)).filter(Boolean);
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { ImpactBadge } from "@/pages/public/EconomicCalendar";

export default function Home() {
  const { sections, trackAndGo, site } = useSite();
  const [content, setContent] = useState({ news: [], education: [], faq: [], promotions: [], testimonials: [], hero_slides: [], videos: [], events: [], trading_ideas: [] });

  useEffect(() => {
    Promise.all([
      api.get("/content/news?published_only=true"),
      api.get("/content/education?published_only=true"),
      api.get("/content/faq?published_only=true"),
      api.get("/content/promotions?published_only=true"),
      api.get("/content/testimonials?published_only=true"),
      api.get("/content/hero_slides?published_only=true"),
      api.get("/content/videos?published_only=true"),
      api.get("/content/events?published_only=true"),
      api.get("/content/trading_ideas?published_only=true"),
    ]).then(([n, e, f, p, t, h, v, ev, ti]) => setContent({ news: n.data, education: e.data, faq: f.data, promotions: p.data, testimonials: t.data, hero_slides: h.data, videos: v.data, events: ev.data, trading_ideas: ti.data })).catch(() => {});
  }, []);

  const enabled = (key) => sections.find((s) => s.key === key && s.enabled);
  const sec = (key) => sections.find((s) => s.key === key) || {};
  const now = new Date();
  const activePromos = content.promotions.filter((p) => !p.end_date || new Date(p.end_date) >= now);

  return (
    <div className="bg-white">
      {enabled("hero") && <HeroSlider data={sec("hero")} slides={content.hero_slides} trackAndGo={trackAndGo} motion={site.hero_motion} />}
      <MarketHighlights />
      {enabled("banking") && <BankingPanel data={sec("banking")} trackAndGo={trackAndGo} />}
      {enabled("trading") && <IconPanel data={sec("trading")} title="Trading" sub="Master asset classes. One powerful platform." testid="trading-panel" trackAndGo={trackAndGo} items={TRADING_ITEMS} cols="lg:grid-cols-4" />}
      {enabled("platforms") && <PlatformsBlock data={sec("platforms")} trackAndGo={trackAndGo} />}
      {enabled("automated") && <FeaturePanel data={sec("automated")} title="Automated Trading & AI" sub="Trade smarter with automation and AI-driven strategies." testid="automated-panel" image={SEC_IMG.automated} cta="Explore Automated Trading" ctaKey="mt5_download" defaults={AUTOMATED_ITEMS} trackAndGo={trackAndGo} />}
      {enabled("wealth") && <FeaturePanel data={sec("wealth")} title="Wealth Management" sub="Grow and preserve your wealth." testid="wealth-panel" image={SEC_IMG.wealth} cta="Explore Investments" ctaKey="client_portal" defaults={WEALTH_ITEMS} trackAndGo={trackAndGo} tint />}
      {enabled("research_tools") && <FeaturePanel data={sec("research_tools")} title="Research & Tools" sub="Make informed decisions with powerful insights." testid="research-panel" image={SEC_IMG.research_tools} cta="Explore Research" ctaKey="/education" defaults={RESEARCH_ITEMS} trackAndGo={trackAndGo} />}
      <FundingConverterBand />
      {enabled("rewards") && <FeaturePanel data={sec("rewards")} title="Bonus & Rewards" sub="More trading, more rewards." testid="bonus-rewards" image={SEC_IMG.rewards} cta="View All Rewards" ctaKey="live_register" defaults={BONUS_ITEMS} trackAndGo={trackAndGo} tint />}
      {enabled("funding") && <FeaturePanel data={sec("funding")} title="Funding & Payments" sub="Fast, secure, convenient." testid="funding-panel" image={SEC_IMG.funding} cta="View Funding Options" ctaKey="deposit" defaults={FUNDING_ITEMS} trackAndGo={trackAndGo} cols="sm:grid-cols-2 lg:grid-cols-3" />}
      {enabled("institutional") && <FeaturePanel data={sec("institutional")} title="Institutional / B2B" sub="Tailored solutions for institutions and partners." testid="institutional-panel" image={SEC_IMG.institutional} cta="Contact Institutional Team" ctaKey="contact" defaults={INSTITUTIONAL_ITEMS} trackAndGo={trackAndGo} tint />}
      {enabled("security") && <FeaturePanel data={sec("security")} title="Security & Trust" sub="Your security is our foundation." testid="security-panel" image={SEC_IMG.security} cta="Learn More" ctaKey="/conditions" defaults={SECURITY_ITEMS} trackAndGo={trackAndGo} />}
      {enabled("support") && <FeaturePanel data={sec("support")} title="Support" sub="We're here, 24/7." testid="support-panel" image={SEC_IMG.support} cta="Contact Support" ctaKey="support" defaults={SUPPORT_ITEMS} trackAndGo={trackAndGo} cols="sm:grid-cols-2" />}
      {enabled("news") && <NewsResearchBand news={content.news} ideas={content.trading_ideas} />}
      {enabled("market_calendar") && <MarketCalendarPanel data={sec("market_calendar")} events={content.events} />}
      {enabled("mobile_apps") && <MobileAppsPanel data={sec("mobile_apps")} trackAndGo={trackAndGo} />}
      <RegulationAudit />
      <BullShowcase trackAndGo={trackAndGo} />
      {enabled("cta") && <CtaBlock data={sec("cta")} trackAndGo={trackAndGo} />}
    </div>
  );
}

function SectionShell({ children, tint, id, className = "" }) {
  return <section id={id} className={`py-8 sm:py-10 border-t border-slate-200 ${tint ? "bg-[#F4F5F7]" : "bg-white"} ${className}`}><div className="cb-container">{children}</div></section>;
}
function Heading({ overline, title, subtitle, center }) {
  return (
    <div className={`mb-6 ${center ? "text-center mx-auto max-w-3xl" : "max-w-3xl"}`}>
      {overline && <p className="overline mb-2 text-[11px]">{overline}</p>}
      <h2 className="font-serif font-bold text-xl sm:text-2xl text-[#06122E] tracking-tight border-b-2 border-[#D8AE4E] inline-block pb-1">{title}</h2>
      {subtitle && <p className="text-slate-500 mt-2 text-sm leading-relaxed">{subtitle}</p>}
    </div>
  );
}

/* Hero — dark navy banner slider (CMS-managed slides) */
function HeroSlider({ data, slides, trackAndGo, motion }) {
  const s0 = (slides && slides.length) ? slides[0] : {};
  const heroImg = s0.image || "/images/dark_hero_matterhorn.jpg";
  const title = s0.title || data.heading || "Trade Global Markets with Confidence";
  const subtitle = s0.subtitle || data.subheading || "Multi-asset trading, banking and wealth management — all on one trusted platform.";
  const [pair, setPair] = useState(HERO_BASE);
  const { live } = useLiveRates();
  useEffect(() => { setPair((p) => mergeLive(p, live)); }, [live]);
  useEffect(() => { const id = setInterval(() => setPair((p) => p.map((r) => ({ ...r, p: tick(r.p) }))), 1500); return () => clearInterval(id); }, []);
  const CTAS = [
    { label: "Open Live Account", key: "live_register", primary: true, testid: "hero-primary-btn" },
    { label: "Try Demo Account", key: "demo_register", testid: "hero-secondary-btn" },
    { label: "Multi-Currency Account", key: "client_portal", testid: "hero-tertiary-btn" },
  ];
  const TRUST = [
    { Icon: ShieldCheck, t: "Trusted & Secure", d: "Your funds, our priority" },
    { Icon: Globe, t: "Global Access", d: "180+ countries" },
    { Icon: Monitor, t: "Advanced Platforms", d: "Trade anywhere" },
    { Icon: TrendingUp, t: "Competitive Spreads", d: "Transparent pricing" },
  ];
  return (
    <section className="relative hero-bg overflow-hidden" data-testid="hero">
      <img src={heroImg} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" />
      {(!motion || motion.enabled !== false) && <MarketMotion speed={(motion && motion.speed) || 0.32} className="absolute inset-0 w-full h-full pointer-events-none opacity-40 mix-blend-screen" />}
      <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(90deg, rgba(5,7,13,0.96) 0%, rgba(5,7,13,0.85) 38%, rgba(5,7,13,0.35) 70%, rgba(5,7,13,0.15) 100%)" }} />
      <div className="absolute inset-0 grid-texture opacity-30 pointer-events-none" />

      {/* Right vertical tagline (mockup) */}
      <div className="hidden xl:flex absolute right-6 top-1/2 -translate-y-1/2 flex-col items-end text-right z-10 max-w-[220px]">
        <span className="text-[10px] uppercase tracking-[0.25em] text-[#D8AE4E] font-bold leading-relaxed">More than just<br />a global partner</span>
        <span className="text-[10px] uppercase tracking-[0.25em] text-slate-400 font-semibold leading-relaxed mt-1">in your financial journey</span>
      </div>

      <div className="cb-container relative z-10 py-16 sm:py-20 lg:py-24">
        <div className="max-w-2xl fade-up">
          <p className="overline mb-3">Institutional Markets</p>
          <h1 className="font-serif font-bold text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-[1.05]">{title}</h1>
          <p className="text-slate-200 text-base sm:text-lg mt-5 max-w-xl leading-relaxed">{subtitle}</p>
          <div className="flex flex-wrap gap-3 mt-8">
            {CTAS.map((c) => (
              <button key={c.testid} onClick={() => trackAndGo(c.key)} data-testid={c.testid}
                className={c.primary
                  ? "btn-gold px-6 py-3.5 inline-flex items-center gap-2 text-sm"
                  : "px-6 py-3.5 inline-flex items-center gap-2 rounded border border-[#D8AE4E]/50 text-[#D8AE4E] font-semibold text-sm hover:bg-[#D8AE4E]/10 transition-colors"}>
                {c.label} {c.primary ? <ArrowRight size={16} /> : <ChevronRight size={16} />}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-10 max-w-2xl" data-testid="hero-trust-row">
            {TRUST.map((it) => (
              <div key={it.t} className="flex items-start gap-2.5" data-testid={`hero-trust-${it.t.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}`}>
                <it.Icon size={20} className="text-[#D8AE4E] shrink-0 mt-0.5" />
                <div className="min-w-0"><p className="text-white text-xs font-semibold leading-tight">{it.t}</p><p className="text-slate-400 text-[11px] mt-0.5 leading-tight">{it.d}</p></div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Live rate strip */}
      <div className="relative z-10 border-t border-white/10 hero-bg-70 backdrop-blur">
        <div className="cb-container flex flex-wrap items-center gap-x-8 gap-y-1 py-3" data-testid="hero-rate-widget">
          <span className="overline inline-flex items-center gap-1.5" data-testid="hero-rate-status">
            <span className={`dot-live ${anyLive(pair) ? "dot-live-on" : ""}`} style={{ background: anyLive(pair) ? "#34d399" : "#fbbf24" }} />
            {anyLive(pair) ? "Live" : "Data delayed"}
          </span>
          {pair.slice(0, 5).map((r) => (
            <span key={r.s} className="inline-flex items-center gap-2 font-mono text-sm">
              <span className="text-slate-400">{r.s}</span><span className="text-white">{r.p}</span>
              <span className={r.d >= 0 ? "text-emerald-400" : "text-red-400"}>{r.d >= 0 ? "+" : ""}{Number(r.d).toFixed(2)}%</span>
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

function OpenAccountsBand({ trackAndGo }) {
  const links = [
    { Icon: UserPlus, label: "Open Live Account", key: "live_register" },
    { Icon: LineChart, label: "Open Demo Account", key: "demo_register" },
    { Icon: Monitor, label: "MT4 Platform", key: "mt4_download" },
    { Icon: Layers, label: "MT5 Platform", key: "mt5_download" },
    { Icon: Wallet, label: "Deposit Funds", key: "deposit" },
    { Icon: LogIn, label: "Client Login", key: "client_login" },
  ];
  return (
    <section className="bg-[#F4F5F7] border-y border-slate-200">
      <div className="cb-container py-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {links.map((l) => (
          <button key={l.key} onClick={() => trackAndGo(l.key)} className="cb-card px-4 py-4 flex flex-col items-center gap-2 text-center" data-testid={`open-acct-${l.key}`}>
            <l.Icon className="gold-text" size={22} /><span className="text-xs text-slate-700 font-medium leading-tight">{l.label}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

function ProductPrograms({ trackAndGo }) {
  const cols = [
    { title: "Open Accounts", img: "/images/prog_accounts.jpg", items: [
      { l: "Open Live Account", key: "live_register" }, { l: "Open Demo Account", key: "demo_register" },
      { l: "MT4 Demo", key: "mt4_download" }, { l: "MT5 Demo", key: "mt5_download" },
      { l: "Multi-Currency Account", key: "client_portal" },
    ] },
    { title: "FX & CFD Trading", img: "/images/prog_trading.jpg", items: [
      { l: "Why Cresent", to: "/conditions" }, { l: "Account Types", to: "/accounts" },
      { l: "Trading Conditions", to: "/conditions" }, { l: "Trading Platforms", to: "/platforms" },
      { l: "Range of Markets", to: "/markets" },
    ] },
    { title: "Crypto & Funding", img: "/images/prog_crypto.jpg", items: [
      { l: "Crypto Markets", to: "/markets" }, { l: "Deposit Funds", key: "deposit" },
      { l: "Withdraw Funds", key: "withdrawal" }, { l: "Wallet", key: "wallet" },
      { l: "Payment Gateway", key: "payment_gateway" },
    ] },
    { title: "Market Research", img: "/images/prog_research.jpg", items: [
      { l: "News & Analysis", to: "/news" }, { l: "Economic Calendar", to: "/economic-calendar" },
      { l: "Education Center", to: "/education" }, { l: "Promotions", to: "/promotions" },
      { l: "FAQ", to: "/faq" },
    ] },
  ];
  const Item = ({ it }) => it.to ? (
    <Link to={it.to} className="flex items-center gap-2 text-sm text-slate-600 hover:text-[#9A7420] py-1.5 transition-colors" data-testid={`program-${it.l.toLowerCase().replace(/\s+/g, "-")}`}><ChevronRight size={13} className="gold-text" /> {it.l}</Link>
  ) : (
    <button onClick={() => trackAndGo(it.key)} className="flex items-center gap-2 text-sm text-slate-600 hover:text-[#9A7420] py-1.5 transition-colors w-full text-left" data-testid={`program-${it.l.toLowerCase().replace(/\s+/g, "-")}`}><ChevronRight size={13} className="gold-text" /> {it.l}</button>
  );
  return (
    <SectionShell>
      <Heading overline="Products & Programs" title="Everything You Need to Trade" subtitle="Open an account, explore markets and access research — all in one place." />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {cols.map((c) => (
          <div key={c.title} className="cb-card overflow-hidden group" data-testid={`program-col-${c.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}`}>
            <div className="relative h-24 overflow-hidden">
              <img src={c.img} alt="" aria-hidden="true" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#06122E]/85 via-[#06122E]/25 to-transparent" />
              <h3 className="absolute bottom-2.5 left-4 font-serif text-base font-bold text-white">{c.title}</h3>
            </div>
            <div className="p-5 pt-3"><div className="divide-y divide-slate-100">{c.items.map((it) => <Item key={it.l} it={it} />)}</div></div>
          </div>
        ))}
      </div>
    </SectionShell>
  );
}

function Widget({ title, action, children, testid }) {
  return (
    <div className="cb-card overflow-hidden" data-testid={testid}>
      <div className="widget-header flex items-center justify-between bg-[#06122E] px-4 py-2.5">
        <h3 className="text-[#D8AE4E] text-xs font-bold uppercase tracking-wider">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

/* Dukascopy-style dashboard band: live quotes panel (left) + sidebar widgets (right) */
function DashboardBand({ trackAndGo }) {
  const cats = Object.keys(INSTRUMENTS);
  const [active, setActive] = useState(cats[0]);
  const { live } = useLiveRates();
  const [rows, setRows] = useState(INSTRUMENTS[cats[0]]);
  useEffect(() => setRows(mergeLive(INSTRUMENTS[active], live)), [active, live]);
  useEffect(() => { const id = setInterval(() => setRows((p) => p.map((r) => ({ ...r, p: tick(r.p) }))), 1700); return () => clearInterval(id); }, [active]);
  const movers = Object.values(INSTRUMENTS).flat().slice().sort((a, b) => Math.abs(b.d) - Math.abs(a.d)).slice(0, 6);
  return (
    <section className="bg-[#F4F5F7] border-t border-slate-200 py-8" data-testid="dashboard-band">
      <div className="cb-container grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        <div className="lg:col-span-8">
          <Widget title="Live Quotes" testid="dash-quotes" action={<Link to="/markets" className="text-[#D8AE4E] text-xs hover:underline">Full list ›</Link>}>
            <div className="flex flex-wrap gap-1.5 p-3 border-b border-slate-100 bg-white">
              {cats.map((c) => <button key={c} onClick={() => setActive(c)} className={`px-3 py-1 text-xs rounded transition-colors ${active === c ? "btn-gold" : "text-slate-600 border border-slate-200 hover:border-[#D8AE4E]/60"}`} data-testid={`dash-cat-${c.toLowerCase()}`}>{c}</button>)}
            </div>
            <div className="overflow-x-auto admin-scroll">
              <table className="w-full text-sm min-w-[520px]" data-testid="dash-quotes-table">
                <thead><tr className="bg-[#0B1D40] text-[#D8AE4E]">
                  {["Instrument", "Bid", "Ask", "24h", "Trend", ""].map((h, i) => <th key={i} className={`px-3 py-2 text-[10px] font-semibold uppercase tracking-wider ${i === 0 ? "text-left" : i === 4 ? "text-center hidden md:table-cell" : "text-right"}`}>{h}</th>)}
                </tr></thead>
                <tbody>{rows.map((r, i) => { const q = quote(r); return (
                  <tr key={r.s} className={`${i % 2 ? "bg-[#FAFAFA]" : "bg-white"} border-b border-slate-100 hover:bg-amber-50/60 transition-colors`}>
                    <td className="px-3 py-2 font-mono font-medium text-[#0B1D40]">{r.s}</td>
                    <td className="px-3 py-2 font-mono text-emerald-600 text-right">{q.bid}</td>
                    <td className="px-3 py-2 font-mono text-red-600 text-right">{q.ask}</td>
                    <td className={`px-3 py-2 font-mono text-right ${r.d >= 0 ? "text-emerald-600" : "text-red-600"}`}>{r.d >= 0 ? "+" : ""}{r.d}%</td>
                    <td className="px-3 py-2 text-center hidden md:table-cell"><Sparkline value={r.p} up={r.d >= 0} width={56} height={20} /></td>
                    <td className="px-3 py-2 text-right"><button onClick={() => trackAndGo("live_register")} className="btn-outline-gold px-3 py-1 text-[11px]" data-testid={`dash-trade-${r.s.replace(/\W/g, "")}`}>Trade</button></td>
                  </tr>); })}</tbody>
              </table>
            </div>
            <div className="flex items-center justify-between px-4 py-2 bg-white border-t border-slate-100 text-[11px] text-slate-400">
              <span className="inline-flex items-center gap-1.5"><span className={`w-1.5 h-1.5 rounded-full ${anyLive(rows) ? "bg-emerald-500 animate-pulse" : "bg-amber-400"}`} />{anyLive(rows) ? "Live" : "Data delayed"}</span>
              <span className="font-mono">{new Date().toUTCString().slice(5, 22)} GMT</span>
            </div>
          </Widget>
        </div>
        <aside className="lg:col-span-4 space-y-6">
          <div className="cb-card-dark p-5 text-center">
            <p className="text-white font-serif text-lg font-bold">Open Your Account</p>
            <p className="text-slate-400 text-xs mt-1 mb-4">Live or demo — ready in minutes.</p>
            <button onClick={() => trackAndGo("live_register")} className="btn-gold w-full py-2.5 text-sm mb-2" data-testid="dash-open-live">Open Live Account</button>
            <button onClick={() => trackAndGo("demo_register")} className="btn-outline-gold w-full py-2.5 text-sm" data-testid="dash-open-demo">Try Free Demo</button>
          </div>
          <Widget title="Top Movers" testid="dash-movers">
            <div className="divide-y divide-slate-100 bg-white">{movers.map((m) => (
              <div key={m.s} className="flex items-center justify-between px-4 py-2 text-sm">
                <span className="font-mono text-[#0B1D40]">{m.s}</span>
                <span className={`font-mono ${m.d >= 0 ? "text-emerald-600" : "text-red-600"}`}>{m.d >= 0 ? "▲" : "▼"} {m.d >= 0 ? "+" : "−"}{Math.abs(m.d).toFixed(2)}%</span>
              </div>))}</div>
          </Widget>
        </aside>
      </div>
    </section>
  );
}

function LiveMarkets({ data, trackAndGo }) {
  const cats = Object.keys(INSTRUMENTS);
  const [tab, setTab] = useState("quotes");
  const [active, setActive] = useState(cats[0]);
  const { live } = useLiveRates();
  const [rows, setRows] = useState(INSTRUMENTS[cats[0]]);
  useEffect(() => { setRows(mergeLive(INSTRUMENTS[active], live)); }, [active, live]);
  useEffect(() => { const id = setInterval(() => setRows((p) => p.map((r) => ({ ...r, p: tick(r.p) }))), 1700); return () => clearInterval(id); }, [active]);
  const [period, setPeriod] = useState("Day");
  const pmul = { Day: 1, Month: 1.7, Quarter: 2.5 }[period];
  const isLive = anyLive(rows);
  return (
    <SectionShell>
      <Heading overline="Live Prices" title={data.heading || "Market Overview"} subtitle={data.subheading} />
      <div className="flex items-center gap-1 mb-5 bg-slate-100 p-1 rounded-lg w-fit">
        {[["quotes", "Quotes"], ["spreads", "Spreads (Avg, pip)"]].map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} className={`px-4 py-2 text-xs rounded transition-colors ${tab === v ? "bg-[#06122E] text-[#D8AE4E] font-bold shadow-sm" : "text-slate-600 hover:text-[#0B1D40] font-medium"}`} data-testid={`market-tab-${v}`}>{l}</button>
        ))}
      </div>
      {tab === "quotes" ? (
        <>
          <div className="flex flex-wrap gap-2 mb-4">
            {cats.map((c) => <button key={c} onClick={() => setActive(c)} className={`px-4 py-1.5 text-xs rounded transition-colors ${active === c ? "btn-gold" : "text-slate-600 border border-slate-200 hover:border-[#D8AE4E]/60"}`} data-testid={`market-cat-${c.toLowerCase()}`}>{c}</button>)}
          </div>
          <div className="cb-card overflow-x-auto admin-scroll p-0">
            <table className="w-full text-sm min-w-[560px]" data-testid="live-rates-table">
              <thead><tr className="bg-[#06122E] text-[#D8AE4E]">
                <th className="px-4 py-2.5 text-[11px] font-sans font-semibold uppercase tracking-wider text-left">Instrument</th><th className="px-4 py-2.5 text-[11px] font-sans font-semibold uppercase tracking-wider text-right">Bid</th><th className="px-4 py-2.5 text-[11px] font-sans font-semibold uppercase tracking-wider text-right">Ask</th><th className="px-4 py-2.5 text-[11px] font-sans font-semibold uppercase tracking-wider text-right hidden sm:table-cell">Vol, mio</th><th className="px-4 py-2.5 text-[11px] font-sans font-semibold uppercase tracking-wider text-right hidden sm:table-cell">24h</th><th className="px-4 py-2.5 text-[11px] font-sans font-semibold uppercase tracking-wider text-center hidden md:table-cell">Trend</th><th className="px-4 py-2.5 text-[11px] font-sans font-semibold uppercase tracking-wider text-right">Action</th>
              </tr></thead>
              <tbody>
                {rows.map((r, i) => { const q = quote(r); return (
                  <tr key={r.s} className={`${i % 2 ? "bg-[#FAFAFA]" : "bg-white"} border-b border-slate-100 hover:bg-amber-50/60 transition-colors`}>
                    <td className="px-4 py-2.5 font-mono font-medium text-[#0B1D40]">{r.s}</td>
                    <td className="px-4 py-2.5 font-mono text-emerald-600 text-right">{q.bid}</td>
                    <td className="px-4 py-2.5 font-mono text-red-600 text-right">{q.ask}</td>
                    <td className="px-4 py-2.5 font-mono text-slate-500 text-right hidden sm:table-cell">{q.vol}</td>
                    <td className={`px-4 py-2.5 font-mono text-right hidden sm:table-cell ${r.d >= 0 ? "text-emerald-600" : "text-red-600"}`}>{r.d >= 0 ? "+" : ""}{r.d}%</td>
                    <td className="px-4 py-2.5 text-center hidden md:table-cell" data-testid={`spark-${r.s.replace(/\W/g, "")}`}><Sparkline value={r.p} up={r.d >= 0} /></td>
                    <td className="px-4 py-2.5 text-right"><button onClick={() => trackAndGo("live_register")} className="btn-outline-gold px-4 py-1 text-xs" data-testid={`trade-${r.s.replace(/\W/g, "")}`}>Trade</button></td>
                  </tr>); })}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between mt-3 text-xs">
            <Link to="/markets" className="inline-flex items-center gap-1 text-[#9A7420] font-semibold hover:gap-2 transition-all" data-testid="rates-full-list">Full list <ChevronRight size={13} /></Link>
            <span className="font-mono text-slate-400" data-testid="rates-timestamp">{new Date().toUTCString().slice(5, 22)} GMT</span>
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-4 text-xs">
            <span className="text-slate-500">Period:</span>
            {["Day", "Month", "Quarter"].map((p) => (
              <button key={p} onClick={() => setPeriod(p)} className={`px-3 py-1 rounded transition-colors ${period === p ? "bg-[#06122E] text-[#D8AE4E] font-semibold" : "text-slate-600 hover:text-[#0B1D40] border border-slate-200"}`} data-testid={`period-${p.toLowerCase()}`}>{p}</button>
            ))}
          </div>
          <div className="cb-card overflow-x-auto admin-scroll p-0">
            <table className="w-full text-sm min-w-[520px]">
              <thead><tr className="bg-[#06122E] text-[#D8AE4E]"><th className="px-4 py-2.5 text-[11px] font-sans font-semibold uppercase tracking-wider text-left">Sessions</th><th className="px-4 py-2.5 text-[11px] font-sans font-semibold uppercase tracking-wider text-right">European</th><th className="px-4 py-2.5 text-[11px] font-sans font-semibold uppercase tracking-wider text-right">US</th><th className="px-4 py-2.5 text-[11px] font-sans font-semibold uppercase tracking-wider text-right">Asian</th></tr></thead>
              <tbody>{SPREADS.map((r, i) => (
                <tr key={r.s} className={`${i % 2 ? "bg-[#FAFAFA]" : "bg-white"} border-b border-slate-100 hover:bg-amber-50/60 transition-colors`}>
                  <td className="px-4 py-2.5 font-mono font-medium text-[#0B1D40]">{r.s}</td><td className="px-4 py-2.5 font-mono text-[#0B1D40] text-right">{(r.eu * pmul).toFixed(2)}</td><td className="px-4 py-2.5 font-mono text-[#0B1D40] text-right">{(r.us * pmul).toFixed(2)}</td><td className="px-4 py-2.5 font-mono text-[#0B1D40] text-right">{(r.asia * pmul).toFixed(2)}</td>
                </tr>))}</tbody>
            </table>
          </div>
        </>
      )}
      <p className="text-xs text-slate-400 mt-4 inline-flex items-center gap-1.5" data-testid="market-data-status">
        <span className={`w-1.5 h-1.5 rounded-full ${tab === "quotes" && isLive ? "bg-emerald-500 animate-pulse" : "bg-amber-400"}`} />
        {tab === "quotes" && isLive
          ? "Live prices streamed from our market data provider. Other instruments are indicative."
          : "Data delayed — indicative values shown. Live pricing streams on the trading platform."}
      </p>
    </SectionShell>
  );
}

/* Dukascopy-style sidebar band: boxed Funding Methods + Currency Converter widgets */
const FUNDING = [
  { img: "/images/pay_visa.jpg", l: "Visa", sub: "Instant · Credit / Debit" },
  { img: "/images/pay_mastercard.jpg", l: "Mastercard", sub: "Instant · Credit / Debit" },
  { img: "/images/pay_bank.jpg", l: "Bank Transfer", sub: "Wire · 1–3 business days" },
  { img: "/images/pay_crypto.jpg", l: "Crypto", sub: "BTC · ETH · USDT" },
  { img: "/images/pay_ewallet.jpg", l: "e-Wallets", sub: "Skrill · Neteller" },
];
const CCY_META = {
  USD: { n: "US Dollar" }, EUR: { n: "Euro" }, GBP: { n: "British Pound" }, CHF: { n: "Swiss Franc" },
  JPY: { n: "Japanese Yen" }, AUD: { n: "Australian Dollar" }, CAD: { n: "Canadian Dollar" },
  XAU: { n: "Gold (oz)" }, BTC: { n: "Bitcoin" }, ETH: { n: "Ethereum" },
};
function useConverterRates() {
  const { live } = useLiveRates();
  const rows = mergeLive(FLAT_ALL, live);
  const px = {};
  rows.forEach((r) => { px[r.s] = Number(r.p) || 0; });
  const per = { USD: 1 };
  if (px["EUR/USD"]) per.EUR = px["EUR/USD"];
  if (px["GBP/USD"]) per.GBP = px["GBP/USD"];
  if (px["AUD/USD"]) per.AUD = px["AUD/USD"];
  if (px["USD/JPY"]) per.JPY = 1 / px["USD/JPY"];
  if (px["USD/CHF"]) per.CHF = 1 / px["USD/CHF"];
  if (px["USD/CAD"]) per.CAD = 1 / px["USD/CAD"];
  if (px["XAU/USD"]) per.XAU = px["XAU/USD"];
  if (px["BTC/USD"]) per.BTC = px["BTC/USD"];
  if (px["ETH/USD"]) per.ETH = px["ETH/USD"];
  return per;
}
function CcyBadge({ c }) {
  return <span className="w-7 h-7 rounded-full bg-[#06122E] text-[#D8AE4E] text-[10px] font-bold font-mono flex items-center justify-center shrink-0 border border-[#D8AE4E]/40">{c}</span>;
}
function CurrencyConverter() {
  const rates = useConverterRates();
  const codes = Object.keys(CCY_META);
  const [amount, setAmount] = useState("1000");
  const [from, setFrom] = useState("EUR");
  const [to, setTo] = useState("USD");
  const a = parseFloat(amount) || 0;
  const rate = rates[from] && rates[to] ? rates[from] / rates[to] : 0;
  const out = a * rate;
  const fmt = (v) => (v >= 1000 ? v.toLocaleString(undefined, { maximumFractionDigits: 2 }) : v >= 1 ? v.toFixed(2) : v.toPrecision(4));
  const sel = "w-full bg-white border border-slate-200 rounded px-3 py-2.5 pl-11 text-sm font-mono text-[#0B1D40] focus:outline-none focus:border-[#D8AE4E] appearance-none cursor-pointer";
  return (
    <Widget title="Currency Converter" testid="currency-converter" action={<span className="text-slate-400 text-[10px] font-mono">indicative rates</span>}>
      <div className="bg-[#FAFAFA] p-4 space-y-3">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Amount</label>
          <input type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1 w-full bg-white border border-slate-200 rounded px-3 py-2.5 text-sm font-mono text-[#0B1D40] focus:outline-none focus:border-[#D8AE4E]" data-testid="conv-amount" />
        </div>
        <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-end">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">From</label>
            <div className="relative mt-1">
              <span className="absolute left-2 top-1/2 -translate-y-1/2"><CcyBadge c={from} /></span>
              <select value={from} onChange={(e) => setFrom(e.target.value)} className={sel} data-testid="conv-from">{codes.map((c) => <option key={c} value={c}>{`${c} — ${CCY_META[c].n}`}</option>)}</select>
            </div>
          </div>
          <button onClick={() => { setFrom(to); setTo(from); }} className="w-9 h-9 mb-0.5 rounded-full border border-slate-200 bg-white flex items-center justify-center text-slate-500 hover:border-[#D8AE4E] hover:text-[#9A7420] transition-colors" aria-label="Swap currencies" data-testid="conv-swap"><ArrowLeftRight size={14} /></button>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">To</label>
            <div className="relative mt-1">
              <span className="absolute left-2 top-1/2 -translate-y-1/2"><CcyBadge c={to} /></span>
              <select value={to} onChange={(e) => setTo(e.target.value)} className={sel} data-testid="conv-to">{codes.map((c) => <option key={c} value={c}>{`${c} — ${CCY_META[c].n}`}</option>)}</select>
            </div>
          </div>
        </div>
        <div className="rounded border border-[#D8AE4E]/40 bg-[#06122E] px-4 py-3">
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{fmt(a)} {from} =</p>
          <p className="text-[#D8AE4E] font-mono text-2xl font-bold mt-0.5" data-testid="conv-result">{fmt(out)} <span className="text-sm text-slate-300">{to}</span></p>
        </div>
        <p className="text-[11px] text-slate-400 font-mono" data-testid="conv-rate">1 {from} = {rate ? fmt(rate) : "—"} {to}</p>
      </div>
    </Widget>
  );
}
function FundingConverterBand() {
  return (
    <section className="bg-[#F4F5F7] border-b border-slate-200 py-8" data-testid="funding-converter-band">
      <div className="cb-container grid lg:grid-cols-12 gap-6 items-start">
        <div className="lg:col-span-7">
          <Widget title="Funding Methods" testid="funding-widget" action={<Link to="/conditions" className="text-[#D8AE4E] text-xs hover:underline">Fees &amp; limits ›</Link>}>
            <div className="bg-white divide-y divide-slate-100">
              {FUNDING.map((m) => (
                <div key={m.l} className="flex items-center gap-3 px-4 py-2.5 hover:bg-amber-50/60 transition-colors" data-testid={`funding-${m.l.toLowerCase().split(" ")[0]}`}>
                  <span className="w-14 h-9 rounded border border-slate-200 bg-[#FAFAFA] flex items-center justify-center shrink-0"><img src={m.img} alt={m.l} className="w-8 h-8 object-contain" loading="lazy" /></span>
                  <span className="text-sm font-medium text-[#0B1D40]">{m.l}</span>
                  <span className="ml-auto text-[11px] text-slate-400 font-mono">{m.sub}</span>
                </div>
              ))}
            </div>
            <p className="px-4 py-2 bg-white border-t border-slate-100 text-[11px] text-slate-400">Deposits &amp; withdrawals are processed via the client portal. Third-party charges may apply.</p>
          </Widget>
        </div>
        <div className="lg:col-span-5"><CurrencyConverter /></div>
      </div>
    </section>
  );
}

const TILES = [
  { Icon: Coins, title: "Multi-Asset Access", desc: "FX, Metals, Indices, Stocks & Crypto." },
  { Icon: Zap, title: "Automated Trading", desc: "Deploy strategies on MT4 / MT5." },
  { Icon: Bitcoin, title: "Crypto 24/7", desc: "Trade leading crypto around the clock." },
  { Icon: Headphones, title: "24/5 Support", desc: "Assistance across trading sessions." },
  { Icon: ShieldCheck, title: "Segregated Funds", desc: "Held with tier-1 banking partners." },
  { Icon: Bot, title: "Fast Onboarding", desc: "Open a live account in minutes.", key: "live_register" },
  { Icon: Landmark, title: "Institutional Liquidity", desc: "Aggregated multi-provider pricing." },
  { Icon: Globe, title: "Global Markets", desc: "One account, worldwide access." },
];
function FeatureTiles({ trackAndGo }) {
  return (
    <section className="py-14 sm:py-20 bg-[#F4F5F7]">
      <div className="cb-container grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {TILES.map((t) => (
          <button key={t.title} onClick={() => t.key && trackAndGo(t.key)} className={`cb-card p-5 text-left flex flex-col gap-3 ${t.key ? "" : "cursor-default"}`} data-testid={`feature-tile-${t.title.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="w-10 h-10 rounded border border-slate-200 bg-[#FAFAFA] flex items-center justify-center gold-text shrink-0"><t.Icon size={19} /></div>
            <div><h3 className="text-[#0B1D40] font-semibold text-sm">{t.title}</h3><p className="text-slate-500 text-xs mt-1 leading-relaxed">{t.desc}</p></div>
          </button>
        ))}
      </div>
    </section>
  );
}

const PLATFORMS = [
  { name: "MetaTrader 4", key: "mt4_download", Icon: Monitor, img: "/images/platform_mt4.jpg", desc: "The world's most popular trading platform for FX and CFDs.", features: ["Expert Advisors", "9 timeframes", "One-click trading"] },
  { name: "MetaTrader 5", key: "mt5_download", Icon: Layers, img: "/images/platform_mt5.jpg", desc: "Multi-asset platform with advanced analytics and depth of market.", features: ["21 timeframes", "Depth of Market", "Economic calendar"] },
  { name: "Cresent WebTrader", key: "webtrader", Icon: Globe, img: "/images/platform_webtrader.jpg", desc: "Trade instantly from any browser — no download required.", features: ["Browser-based", "Cross-device sync", "Instant access"] },
];
function PlatformsBlock({ data, trackAndGo }) {
  return (
    <SectionShell>
      <Widget title={data.heading || "Trading Platforms"} testid="platforms-widget" action={<span className="text-slate-300 text-xs hidden sm:inline">Any device · anywhere</span>}>
        <div className="p-5 grid md:grid-cols-3 gap-4 bg-white">
          {PLATFORMS.map((p) => (
            <div key={p.name} className="border border-slate-200 rounded overflow-hidden flex flex-col" data-testid={`platform-card-${p.key}`}>
              <div className="relative h-36 overflow-hidden">
                <img src={p.img} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#06122E]/70 to-transparent" />
                <p.Icon className="absolute bottom-2 left-3 text-[#D8AE4E]" size={22} />
              </div>
              <div className="p-5 flex flex-col flex-1">
              <h3 className="font-serif text-lg font-bold text-[#0B1D40] mb-2">{p.name}</h3>
              <p className="text-slate-500 text-sm leading-relaxed mb-4">{p.desc}</p>
              <ul className="space-y-1.5 mb-5 flex-1">{p.features.map((f) => <li key={f} className="flex items-center gap-2 text-sm text-slate-600"><ChevronRight size={13} className="gold-text" /> {f}</li>)}</ul>
              <button onClick={() => trackAndGo(p.key)} className="btn-outline-gold px-4 py-2 text-sm w-full inline-flex items-center justify-center gap-2" data-testid={`platform-launch-${p.key}`}>Launch / Download <ArrowUpRight size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      </Widget>
    </SectionShell>
  );
}

const RESEARCH = [
  { Icon: LineChart, title: "Technical Analysis", img: "/images/edu_candles.jpg", desc: "Chart patterns, indicators and key levels across major instruments." },
  { Icon: TrendingUp, title: "Expert Commentary", img: "/images/hero_gold_bull.jpg", desc: "Timely market views and macro insight from our analyst desk." },
  { Icon: Layers, title: "Trade Pattern Ideas", img: "/images/chart_bull.jpg", desc: "Actionable setups with defined entries, targets and invalidation." },
  { Icon: Globe, title: "Fundamental Analysis", img: "/images/globe_3d_gold.jpg", desc: "Central bank policy, data releases and cross-asset drivers." },
];
function MarketResearchProducts() {
  return (
    <SectionShell tint>
      <Widget title="Market Research Products" testid="research-widget" action={<Link to="/education" className="text-[#D8AE4E] text-xs hover:underline">Explore ›</Link>}>
        <div className="p-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 bg-white">
          {RESEARCH.map((r) => (
            <Link key={r.title} to="/education" className="border border-slate-200 rounded overflow-hidden group hover:border-[#D8AE4E]/60 transition-colors" data-testid={`research-${r.title.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="relative h-24 overflow-hidden">
                <img src={r.img} alt="" aria-hidden="true" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#06122E]/70 to-transparent" />
                <r.Icon className="absolute bottom-2 left-3 text-[#D8AE4E]" size={20} />
              </div>
              <div className="p-4">
              <h3 className="text-[#0B1D40] font-semibold mb-2 flex items-center gap-1.5 text-sm">{r.title}<ArrowUpRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity gold-text" /></h3>
              <p className="text-slate-500 text-sm leading-relaxed">{r.desc}</p>
              </div>
            </Link>
          ))}
        </div>
      </Widget>
    </SectionShell>
  );
}

const REG_ITEMS = [
  { img: "/images/seal_regulated.jpg", title: "Licensed & Regulated", desc: "Authorised under a tier-1 financial markets regulator." },
  { img: "/images/seal_funds.jpg", title: "Segregated Client Funds", desc: "Client money held separately with tier-1 banking partners." },
  { img: "/images/seal_audited.jpg", title: "Independent External Audit", desc: "Accounts audited annually by a global assurance firm." },
  { img: "/images/seal_member.jpg", title: "Markets Association Member", desc: "Committed to international compliance and AML standards." },
];
function RegulationAudit() {
  return (
    <section className="py-14 sm:py-20 bg-[#06122E]">
      <div className="cb-container">
        <div className="text-center mb-12">
          <p className="overline" style={{ color: "#D8AE4E" }}>Regulation &amp; Audit</p>
          <h2 className="font-serif text-3xl sm:text-4xl text-white mt-3">Swiss-grade trust and oversight</h2>
          <p className="text-slate-400 text-sm mt-3">Indicative trust marks — swapped for our official regulator badges on request.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {REG_ITEMS.map((r) => (
            <div key={r.title} className="rounded-xl border border-[#203E70] bg-[#0B1D40]/60 p-7 text-center flex flex-col items-center hover:border-[#D8AE4E]/50 transition-colors" data-testid={`reg-${r.title.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="w-20 h-20 rounded-full bg-white overflow-hidden flex items-center justify-center mb-4 ring-2 ring-[#D8AE4E]/40 shadow-md">
                <img src={r.img} alt={r.title} className="w-full h-full object-contain p-1" loading="lazy" />
              </div>
              <h3 className="text-white font-semibold mb-2">{r.title}</h3>
              <p className="text-slate-400 text-sm leading-relaxed">{r.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const COND = [
  { Icon: Zap, img: "/images/cond_latency.jpg", title: "Ultra-Low Latency", desc: "Execution engineered for speed under 10ms." },
  { Icon: ShieldCheck, img: "/images/cond_funds.jpg", title: "Segregated Funds", desc: "Client funds held with tier-1 banking partners." },
  { Icon: Layers, img: "/images/cond_liquidity.jpg", title: "Deep Liquidity", desc: "Aggregated pricing from multiple providers." },
  { Icon: TrendingUp, img: "/images/cond_pricing.jpg", title: "Transparent Pricing", desc: "Raw spreads with no hidden markup." },
];
function ConditionsBlock({ data }) {
  return (
    <SectionShell>
      <Heading overline="Why Cresent" title={data.heading || "Trading Conditions"} subtitle={data.subheading} center />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {COND.map((c) => (
          <div key={c.title} className="cb-card overflow-hidden group" data-testid={`condition-${c.title.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="relative h-32 overflow-hidden">
              <img src={c.img} alt="" aria-hidden="true" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#06122E]/80 via-[#06122E]/20 to-transparent" />
              <c.Icon className="absolute bottom-2.5 left-3 text-[#D8AE4E]" size={22} />
            </div>
            <div className="p-5"><h3 className="text-[#0B1D40] font-semibold mb-2">{c.title}</h3><p className="text-slate-500 text-sm leading-relaxed">{c.desc}</p></div>
          </div>
        ))}
      </div>
    </SectionShell>
  );
}

function StatsBlock({ data }) {
  const stats = (data.config && data.config.stats) || [];
  const ref = useRef(null);
  const [started, setStarted] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || !stats.length) return;
    const ob = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setStarted(true); ob.disconnect(); } }, { threshold: 0.35 });
    ob.observe(el);
    return () => ob.disconnect();
  }, [stats.length]);
  if (!stats.length) return null;
  return (
    <section ref={ref} className="py-16 bg-[#06122E] grid-texture">
      <div className="cb-container grid sm:grid-cols-2 lg:grid-cols-4 gap-8 text-center">
        {stats.map((s, i) => (
          <div key={i} data-testid={`stat-${i}`} className="fade-up" style={{ animationDelay: `${i * 0.12}s` }}>
            <p className="font-serif text-4xl sm:text-5xl text-[#D8AE4E] font-bold tracking-tight"><CountUp value={s.value} run={started} /></p>
            <p className="text-slate-300 text-sm mt-2">{s.label}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function CountUp({ value, run }) {
  const m = String(value ?? "").match(/^([^\d]*)([\d.,]+)(.*)$/);
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!run || !m) return;
    const target = parseFloat(m[2].replace(/,/g, ""));
    const t0 = performance.now();
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - t0) / 1400);
      setN(target * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [run]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!m) return <>{value}</>;
  const dec = ((m[2].split(".")[1]) || "").length;
  return <>{m[1]}{n.toLocaleString(undefined, { minimumFractionDigits: dec, maximumFractionDigits: dec })}{m[3]}</>;
}

function PromotionsBlock({ data, items, trackAndGo }) {
  return (
    <SectionShell tint>
      <Heading overline="Offers" title={data.heading || "Current Promotions"} subtitle={data.subheading} />
      <div className="grid md:grid-cols-2 gap-6">
        {items.map((p) => (
          <div key={p.id} className="cb-card p-8 flex flex-col" data-testid={`promo-${p.id}`}>
            {p.badge && <span className="overline mb-3">{p.badge}</span>}
            <h3 className="font-serif text-2xl text-[#0B1D40] mb-2">{p.title}</h3>
            <p className="text-slate-500 text-sm leading-relaxed flex-1">{p.description}</p>
            {p.end_date && <p className="text-xs text-slate-400 mt-4 font-mono">Ends {new Date(p.end_date).toLocaleDateString()}</p>}
            <button onClick={() => trackAndGo(p.link_key || "live_register")} className="btn-gold px-6 py-3 text-sm mt-5 self-start">Claim Offer</button>
          </div>
        ))}
      </div>
    </SectionShell>
  );
}

function NewsResearchBand({ news, ideas }) {
  const nn = (news || []).slice(0, 5);
  const ii = (ideas || []).slice(0, 5);
  if (!nn.length && !ii.length) return null;
  return (
    <section className="bg-[#F4F5F7] border-t border-slate-200 py-8" data-testid="news-research-band">
      <div className="cb-container grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        <Widget title="Company News" testid="band-news" action={<Link to="/news" className="text-[#D8AE4E] text-xs hover:underline">All news ›</Link>}>
          <div className="divide-y divide-slate-100 bg-white">
            {nn.length ? nn.map((n) => (
              <Link key={n.id} to={`/news/${n.id}`} className="flex items-start gap-3 px-4 py-3 hover:bg-amber-50/60 transition-colors">
                <span className="shrink-0 font-mono text-[11px] text-slate-400 w-14">{n.created_at ? new Date(n.created_at).toLocaleDateString(undefined, { day: "2-digit", month: "short" }) : "—"}</span>
                <span className="text-[13px] text-[#0B1D40] leading-snug">{n.title}</span>
              </Link>
            )) : <p className="px-4 py-6 text-sm text-slate-400">No news yet.</p>}
          </div>
        </Widget>
        <Widget title="Market Research" testid="band-research-ideas" action={<Link to="/news" className="text-[#D8AE4E] text-xs hover:underline">All ideas ›</Link>}>
          <div className="divide-y divide-slate-100 bg-white">
            {ii.length ? ii.map((t) => (
              <Link key={t.id} to={`/trading-ideas/${t.id}`} className="flex items-start gap-3 px-4 py-3 hover:bg-amber-50/60 transition-colors">
                <span className="shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded bg-[#06122E] text-[#D8AE4E]">{t.instrument || "Idea"}</span>
                <span className="text-[13px] text-[#0B1D40] leading-snug">{t.title}</span>
              </Link>
            )) : <p className="px-4 py-6 text-sm text-slate-400">No research yet.</p>}
          </div>
        </Widget>
      </div>
    </section>
  );
}

function NewsBlock({ data, items }) {
  return (
    <SectionShell>
      <div className="flex items-end justify-between mb-10">
        <Heading overline="Insights" title={data.heading || "Market News & Insights"} subtitle={data.subheading} />
        <Link to="/news" className="hidden sm:inline-flex items-center gap-1 text-sm gold-text hover:gap-2 transition-all" data-testid="news-view-all">View all <ArrowRight size={16} /></Link>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {items.map((n) => (
          <Link to={`/news/${n.id}`} key={n.id} className="cb-card p-6 flex gap-5 items-start" data-testid={`news-card-${n.id}`}>
            <div className="shrink-0 w-16 text-center border-r border-slate-200 pr-4"><p className="font-mono text-xs text-slate-400">{n.created_at ? new Date(n.created_at).toLocaleDateString(undefined, { day: "2-digit", month: "short" }) : "—"}</p></div>
            <div className="min-w-0">{n.category && <span className="overline">{n.category}</span>}<h3 className="font-serif text-xl text-[#0B1D40] mt-1 mb-2">{n.title}</h3><p className="text-slate-500 text-sm leading-relaxed line-clamp-2">{n.excerpt}</p></div>
          </Link>
        ))}
      </div>
    </SectionShell>
  );
}

const IDEAS = {
  "EUR/USD": [{ d: "25 Jun", t: "EUR/USD Outlook", x: "Sample analysis: after rejection near 1.0900, price eases toward the 1.0800 support zone." }, { d: "16 Jun", t: "EUR/USD Market Outlook", x: "Sample analysis: pair recovers toward 1.0870 amid low volatility ahead of policy data." }],
  "USD/JPY": [{ d: "19 May", t: "USD/JPY Outlook", x: "Sample analysis: recovery from recent lows targets the 158.00 handle." }, { d: "14 Apr", t: "USD/JPY Consolidation", x: "Sample analysis: consolidation near 156.00 with focus on central bank guidance." }],
  "GBP/USD": [{ d: "26 Aug", t: "GBP/USD Outlook", x: "Sample analysis: softer dollar lifts cable toward the 1.2750 area." }, { d: "30 Jul", t: "GBP/USD Retrace", x: "Sample analysis: pair retraces from highs toward 1.2680 support." }],
  "Gold": [{ d: "01 Jun", t: "XAU/USD Review", x: "Sample analysis: gold stabilises near 2330 as volatility compresses." }, { d: "18 May", t: "Gold Outlook", x: "Sample analysis: stronger dollar pressures gold below near-term resistance." }],
};
function TradingIdeas({ items }) {
  const cms = items || [];
  const useCms = cms.length > 0;
  const groups = {};
  if (useCms) cms.forEach((it) => { const k = it.instrument || "General"; (groups[k] = groups[k] || []).push(it); });
  const data = useCms ? groups : IDEAS;
  const tabs = Object.keys(data);
  const [t, setT] = useState(tabs[0]);
  useEffect(() => { if (!tabs.includes(t)) setT(tabs[0]); }, [tabs.join("|")]); // eslint-disable-line react-hooks/exhaustive-deps
  const norm = (it) => useCms
    ? { d: it.date ? new Date(it.date).toLocaleDateString(undefined, { day: "2-digit", month: "short" }) : (it.created_at ? new Date(it.created_at).toLocaleDateString(undefined, { day: "2-digit", month: "short" }) : ""), t: it.title, x: it.summary || "", id: it.id, bias: it.bias }
    : it;
  const list = (data[t] || []).map(norm);
  return (
    <SectionShell tint>
      <Widget title="Trading Ideas" testid="ideas-widget" action={<Link to="/news" className="text-[#D8AE4E] text-xs hover:underline">All ideas ›</Link>}>
        <div className="bg-white p-5">
          <div className="flex flex-wrap gap-2 mb-5">
            {tabs.map((x) => <button key={x} onClick={() => setT(x)} className={`px-3.5 py-1.5 text-xs rounded transition-colors ${t === x ? "btn-gold" : "text-slate-600 border border-slate-200 hover:border-[#D8AE4E]/60"}`} data-testid={`idea-tab-${x.replace(/\W/g, "")}`}>{x}</button>)}
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {list.map((it, i) => {
              const inner = (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <span className="inline-flex items-center gap-2 text-xs text-slate-400 font-mono"><Calendar size={13} className="gold-text" /> {it.d}</span>
                    {it.bias && <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${/neutral/i.test(it.bias) ? "bg-slate-100 text-slate-600" : /bear/i.test(it.bias) ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-700"}`}>{it.bias}</span>}
                  </div>
                  <h3 className="font-serif font-bold text-base text-[#0B1D40] mb-1">{it.t}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{it.x}</p>
                  {it.id && <span className="inline-flex items-center gap-1 text-xs gold-text mt-3">Read full analysis <ArrowRight size={13} /></span>}
                </>
              );
              return it.id
                ? <Link key={it.id} to={`/trading-ideas/${it.id}`} className="border border-slate-200 rounded p-5 block hover:border-[#D8AE4E]/60 transition-colors" data-testid={`idea-card-${i}`}>{inner}</Link>
                : <div key={i} className="border border-slate-200 rounded p-5" data-testid={`idea-card-${i}`}>{inner}</div>;
            })}
          </div>
        </div>
      </Widget>
    </SectionShell>
  );
}

const EDU_IMGS = ["/images/edu_candles.jpg", "/images/edu_risk.jpg", "/images/edu_webinar.jpg"];
function EducationBlock({ data, items }) {
  return (
    <SectionShell>
      <Heading overline="Learn" title={data.heading || "Education Center"} subtitle={data.subheading} />
      <div className="grid md:grid-cols-3 gap-6">
        {items.map((e, i) => (
          <div key={e.id} className="cb-card overflow-hidden" data-testid={`education-card-${e.id}`}>
            <div className="relative h-32 overflow-hidden">
              <img src={EDU_IMGS[i % EDU_IMGS.length]} alt="" aria-hidden="true" className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#06122E]/60 to-transparent" />
              {e.level && <span className="absolute bottom-2 left-3 text-[10px] px-2 py-0.5 rounded bg-[#06122E]/85 text-[#D8AE4E] font-semibold uppercase tracking-wider border border-[#D8AE4E]/30">{e.level}</span>}
            </div>
            <div className="p-6">
              <div className="flex items-center gap-2 mb-3">{e.read_time && <span className="text-xs text-slate-400 font-mono">{e.read_time}</span>}</div>
              <h3 className="font-serif text-xl text-[#0B1D40] mb-2">{e.title}</h3><p className="text-slate-500 text-sm leading-relaxed">{e.excerpt}</p>
            </div>
          </div>
        ))}
      </div>
      <Link to="/education" className="inline-flex items-center gap-1 text-sm gold-text mt-8">Browse all guides <ArrowRight size={16} /></Link>
    </SectionShell>
  );
}

function ytId(v) {
  if (!v) return "";
  const s = String(v).trim();
  const m = s.match(/(?:v=|youtu\.be\/|embed\/)([\w-]{11})/);
  return m ? m[1] : (/^[\w-]{11}$/.test(s) ? s : "");
}
function LatestVideos({ videos }) {
  const list = (videos || []).map((v) => ({ ...v, _id: ytId(v.youtube_id) }));
  const [open, setOpen] = useState(null);
  if (!list.length) return null;
  return (
    <SectionShell tint>
      <Heading overline="Tutorials" title="Latest Videos" subtitle="Short guides to help you get started." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {list.map((v, i) => (
          <button key={v.id || i} onClick={() => v._id ? setOpen(v._id) : null} className="cb-card overflow-hidden group text-left" data-testid={`video-${i}`}>
            <div className="aspect-video bg-[#06122E] grid-texture flex items-center justify-center relative overflow-hidden">
              {v._id && <img src={`https://img.youtube.com/vi/${v._id}/hqdefault.jpg`} alt={v.title} className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity" />}
              <span className="relative w-12 h-12 rounded-full bg-[#D8AE4E] flex items-center justify-center text-[#06122E] group-hover:scale-110 transition-transform"><Play size={18} className="ml-0.5" /></span>
            </div>
            <div className="p-4"><p className="text-sm text-[#0B1D40] font-medium leading-snug">{v.title}</p></div>
          </button>
        ))}
      </div>
      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-3xl p-0 bg-black border-[#203E70] overflow-hidden">
          <DialogTitle className="sr-only">Video player</DialogTitle>
          {open && <div className="aspect-video w-full"><iframe title="video" className="w-full h-full" src={`https://www.youtube.com/embed/${open}?autoplay=1`} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen /></div>}
        </DialogContent>
      </Dialog>
    </SectionShell>
  );
}

function EconomicCalendarBlock({ events }) {
  const upcoming = [...(events || [])].sort((a, b) => new Date(a.date) - new Date(b.date)).slice(0, 6);
  if (!upcoming.length) return null;
  return (
    <SectionShell>
      <div className="flex items-end justify-between mb-8">
        <Heading overline="Market Research" title="Economic Calendar" subtitle="Upcoming market-moving events and their expected impact." />
        <Link to="/economic-calendar" className="hidden sm:inline-flex items-center gap-1 text-sm gold-text hover:gap-2 transition-all" data-testid="calendar-view-all">Full calendar <ArrowRight size={16} /></Link>
      </div>
      <div className="grid lg:grid-cols-12 gap-6 items-stretch">
        <div className="lg:col-span-4 relative rounded overflow-hidden border border-slate-200 min-h-[240px] group" data-testid="calendar-visual">
          <img src="/images/calendar_macro.jpg" alt="Macro economic events" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#06122E]/95 via-[#06122E]/35 to-transparent" />
          <div className="absolute bottom-0 p-5">
            <p className="overline mb-2" style={{ color: "#D8AE4E" }}>Macro Drivers</p>
            <p className="text-white font-serif text-lg font-bold leading-snug">Central banks, inflation prints and payrolls — the events that move markets.</p>
            <Link to="/economic-calendar" className="inline-flex items-center gap-1 text-xs text-[#D8AE4E] mt-3 hover:gap-2 transition-all" data-testid="calendar-visual-link">Open full calendar <ArrowRight size={13} /></Link>
          </div>
        </div>
        <div className="lg:col-span-8 cb-card overflow-x-auto admin-scroll">
        <table className="w-full text-sm min-w-[600px]">
          <thead><tr className="text-left text-slate-500 border-b border-slate-200">
            <th className="px-6 py-3 font-medium">Date</th><th className="px-6 py-3 font-medium">Cur.</th><th className="px-6 py-3 font-medium">Event</th><th className="px-6 py-3 font-medium">Impact</th><th className="px-6 py-3 font-medium text-right">Forecast</th><th className="px-6 py-3 font-medium text-right">Previous</th>
          </tr></thead>
          <tbody>
            {upcoming.map((e) => (
              <tr key={e.id} className="border-b border-slate-200 last:border-0 hover:bg-[#FAFAFA] transition-colors" data-testid={`home-event-${e.id}`}>
                <td className="px-6 py-4 font-mono text-slate-600 whitespace-nowrap">{e.date ? new Date(e.date).toLocaleDateString(undefined, { day: "2-digit", month: "short" }) : "—"}</td>
                <td className="px-6 py-4 font-mono text-[#0B1D40]">{e.currency}</td>
                <td className="px-6 py-4 text-[#0B1D40]">{e.title}</td>
                <td className="px-6 py-4"><ImpactBadge impact={e.impact} /></td>
                <td className="px-6 py-4 font-mono text-slate-600 text-right">{e.forecast || "-"}</td>
                <td className="px-6 py-4 font-mono text-slate-500 text-right">{e.previous || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </SectionShell>
  );
}

function TestimonialsBlock({ data, items }) {
  return (
    <SectionShell>
      <Heading overline="Clients" title={data.heading || "Client Perspectives"} subtitle={data.subheading} center />
      <div className="grid md:grid-cols-3 gap-6">
        {items.map((t) => (
          <div key={t.id} className="cb-card p-7" data-testid={`testimonial-${t.id}`}>
            <Quote className="gold-text mb-4" size={24} /><p className="text-slate-700 leading-relaxed mb-5">{t.quote}</p>
            <div className="flex items-center gap-3">
              {t.avatar && <img src={t.avatar} alt={t.author} className="w-10 h-10 rounded-full object-cover" />}
              <div><p className="text-[#0B1D40] text-sm font-semibold">{t.author}</p>{t.role && <p className="text-slate-400 text-xs">{t.role}</p>}</div>
              {t.rating && <div className="ml-auto flex gap-0.5">{Array.from({ length: Math.min(5, Number(t.rating) || 0) }).map((_, i) => <Star key={i} size={14} className="text-[#D8AE4E] fill-current" />)}</div>}
            </div>
          </div>
        ))}
      </div>
    </SectionShell>
  );
}

function FaqBlock({ data, items }) {
  return (
    <SectionShell tint>
      <Heading overline="Support" title={data.heading || "Frequently Asked Questions"} subtitle={data.subheading} center />
      <div className="max-w-3xl mx-auto">
        <Accordion type="single" collapsible className="space-y-3">
          {items.map((f) => (
            <AccordionItem key={f.id} value={f.id} className="cb-card px-6 border-b" data-testid={`faq-${f.id}`}>
              <AccordionTrigger className="text-[#0B1D40] hover:no-underline text-left">{f.question}</AccordionTrigger>
              <AccordionContent className="text-slate-500 leading-relaxed">{f.answer}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </SectionShell>
  );
}

function GlobalReach() {
  const globeRef = useParallax(0.18);
  const bullRef = useParallax(0.28);
  const points = [
    { Icon: Globe, t: "Global Markets", d: "Trade across international FX, commodities, indices, stocks and crypto sessions." },
    { Icon: Layers, t: "Institutional Liquidity", d: "Aggregated pricing from multiple providers across major liquidity hubs." },
    { Icon: ShieldCheck, t: "Segregated Infrastructure", d: "Client funds held separately with tier-1 banking partners." },
  ];
  return (
    <section className="py-16 sm:py-20 bg-[#06122E] relative overflow-hidden" data-testid="global-reach-band">
      <div className="cb-container grid lg:grid-cols-2 gap-12 items-center relative z-10">
        <div className="relative">
          <img ref={globeRef} src="/images/globe_3d_gold.jpg" alt="Global markets network" className="rounded-md w-full object-cover border border-[#203E70] will-change-transform" />
          <div className="absolute -bottom-6 -right-4 hidden sm:block w-44">
            <img ref={bullRef} src="/images/bull_gold.png" alt="Golden bull" className="w-full h-full object-contain will-change-transform drop-shadow-[0_14px_30px_rgba(0,0,0,0.55)]" />
          </div>
        </div>
        <div>
          <p className="overline" style={{ color: "#D8AE4E" }}>International Presence</p>
          <h2 className="font-serif font-semibold text-3xl sm:text-4xl text-white tracking-tight mt-3">A Global Markets Institution</h2>
          <p className="text-slate-300 mt-4 leading-relaxed max-w-lg">Serving traders across continents with one account, one liquidity network, and one standard of execution.</p>
          <div className="space-y-5 mt-8">
            {points.map((p) => (
              <div key={p.t} className="flex items-start gap-4">
                <div className="w-11 h-11 rounded border border-[#203E70] bg-[#0B1D40] flex items-center justify-center shrink-0"><p.Icon size={20} style={{ color: "#D8AE4E" }} /></div>
                <div><h3 className="text-white font-semibold text-sm">{p.t}</h3><p className="text-slate-400 text-sm mt-1 leading-relaxed">{p.d}</p></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function BullShowcase({ trackAndGo }) {
  const bullRef = useParallax(0.12);
  return (
    <section className="py-16 sm:py-20 bg-white" data-testid="bull-showcase-band">
      <div className="cb-container grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <p className="overline">Strength & Precision</p>
          <h2 className="font-serif font-semibold text-3xl sm:text-4xl text-[#0B1D40] tracking-tight mt-3">Built on Strength. Driven by Precision.</h2>
          <p className="text-slate-500 mt-4 leading-relaxed max-w-lg">The bull is our emblem — disciplined momentum, unwavering conviction, and institutional standards in everything we build for traders worldwide.</p>
          <div className="flex flex-wrap gap-4 mt-8">
            <button onClick={() => trackAndGo("live_register")} className="btn-gold px-7 py-3.5 inline-flex items-center gap-2" data-testid="bull-cta-live">Open Live Account <ArrowRight size={18} /></button>
            <button onClick={() => trackAndGo("demo_register")} className="btn-outline-gold px-7 py-3.5 inline-flex items-center gap-2" data-testid="bull-cta-demo">Try a Demo</button>
          </div>
        </div>
        <div className="relative flex items-center justify-center">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(216,174,78,0.16),transparent_70%)]" />
          <img ref={bullRef} src="/images/bull_gold.png" alt="Cresent Aurean bull emblem" className="relative w-full max-w-md mx-auto object-contain will-change-transform drop-shadow-[0_24px_48px_rgba(216,174,78,0.28)]" />
        </div>
      </div>
    </section>
  );
}

function CtaBlock({ data, trackAndGo }) {
  return (
    <section className="py-20 grid-texture bg-[#06122E]">
      <div className="cb-container text-center">
        <h2 className="font-serif font-bold text-3xl sm:text-4xl text-white max-w-2xl mx-auto">{data.heading || "Ready to Trade the Global Markets?"}</h2>
        <p className="text-slate-300 mt-4 max-w-xl mx-auto">{data.subheading || "Open your account in minutes."}</p>
        <div className="flex flex-wrap justify-center gap-4 mt-8">
          <button onClick={() => trackAndGo("live_register")} className="btn-gold px-8 py-3.5" data-testid="cta-open-account">Open Live Account</button>
          <button onClick={() => trackAndGo("demo_register")} className="px-8 py-3.5 rounded border border-white/30 text-white font-semibold hover:bg-white/10 transition-colors" data-testid="cta-demo-account">Try Demo</button>
        </div>
      </div>
    </section>
  );
}

/* ── Mockup: Market Highlights band (flag/icon cards + sparkline + session tabs) ── */
const MH_FLAG = {
  "EUR/USD": "🇪🇺", "GBP/USD": "🇬🇧", "USD/JPY": "🇯🇵", "AUD/USD": "🇦🇺", "USD/CHF": "🇨🇭", "USD/CAD": "🇨🇦",
  "XAU/USD": "🥇", "XAG/USD": "🥈", "XPT/USD": "⚪", "BTC/USD": "₿", "ETH/USD": "Ξ", "SOL/USD": "◎",
  "US500": "🇺🇸", "US30": "🇺🇸", "NAS100": "🇺🇸", "GER40": "🇩🇪",
  "AAPL": "", "TSLA": "", "AMZN": "", "MSFT": "", "WTI Oil": "🛢️", "Brent": "🛢️", "NatGas": "🔥",
};
function MarketHighlights() {
  const cats = Object.keys(INSTRUMENTS);
  const [active, setActive] = useState(cats[0]);
  const { live } = useLiveRates();
  const [rows, setRows] = useState(INSTRUMENTS[cats[0]]);
  useEffect(() => setRows(mergeLive(INSTRUMENTS[active], live)), [active, live]);
  useEffect(() => { const id = setInterval(() => setRows((p) => p.map((r) => ({ ...r, p: tick(r.p) }))), 1600); return () => clearInterval(id); }, [active]);
  const cards = (() => {
    const base = rows.slice(0, 6);
    if (base.length >= 6) return base;
    const seen = new Set(base.map((r) => r.s));
    const extra = FLAT_ALL.filter((r) => !seen.has(r.s)).slice(0, 6 - base.length);
    return [...base, ...extra];
  })();
  return (
    <section className="mk-panel bg-[#05070D] border-b border-[#1E2740] py-7" data-testid="market-highlights">
      <div className="cb-container">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="font-serif text-lg font-bold text-white tracking-tight border-b-2 border-[#D8AE4E] pb-1">Market Highlights</h2>
          <div className="flex flex-wrap items-center gap-1.5">
            {cats.map((c) => <button key={c} onClick={() => setActive(c)} className={`px-3 py-1 text-xs rounded transition-colors ${active === c ? "btn-gold" : "text-slate-400 border border-[#1E2740] hover:border-[#D8AE4E]/60 hover:text-[#D8AE4E]"}`} data-testid={`mh-cat-${c.toLowerCase()}`}>{c}</button>)}
            <span className="ml-2 inline-flex items-center gap-1.5 text-[11px] text-emerald-400"><span className={`dot-live ${anyLive(rows) ? "dot-live-on" : ""}`} style={{ background: anyLive(rows) ? "#34d399" : "#fbbf24" }} />{anyLive(rows) ? "Live" : "Delayed"}</span>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="mh-grid">
          {cards.map((r) => { const q = quote(r); return (
            <div key={r.s} className="mh-card p-3.5" data-testid={`mh-card-${r.s.replace(/\W/g, "")}`}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="inline-flex items-center gap-1.5 text-white font-mono text-sm font-medium"><span className="text-base leading-none">{MH_FLAG[r.s] || "•"}</span>{r.s}</span>
                <Sparkline value={r.p} up={r.d >= 0} width={46} height={16} />
              </div>
              <p className="text-white font-mono text-lg leading-none">{r.p}</p>
              <p className={`font-mono text-xs mt-1 ${r.d >= 0 ? "text-emerald-400" : "text-red-400"}`}>{r.d >= 0 ? "▲ +" : "▼ −"}{Math.abs(r.d).toFixed(2)}%</p>
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#1E2740] text-[10px] text-slate-500 font-mono">
                <span>Spread {r.spr}</span><span>Vol {q.vol}</span>
              </div>
            </div>
          ); })}
        </div>
      </div>
    </section>
  );
}

/* ── Mockup: Banking solutions panel ── */
function BankingPanel({ data = {}, trackAndGo }) {
  const list = panelItems(data, BANKING_ITEMS);
  return (
    <SectionShell className="mk-panel">
      <Widget title={data.heading || "Banking"} testid="banking-panel" action={<span className="text-slate-400 text-xs hidden sm:inline">{data.subheading || "Modern banking for global clients"}</span>}>
        <div className="grid lg:grid-cols-12 gap-0 bg-[#0B0F1A]">
          <div className="lg:col-span-4 relative min-h-[220px] overflow-hidden">
            <img src="/images/dark_banking_geneva.jpg" alt="Private banking" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#05070D] via-[#05070D]/40 to-transparent" />
            <button onClick={() => trackAndGo("client_portal")} className="btn-gold px-4 py-2 text-xs absolute bottom-4 left-4" data-testid="banking-learn">Learn More</button>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-px bg-[#1E2740]">
            {list.map((it, i) => <PanelItem key={i} it={it} testid="banking" trackAndGo={trackAndGo} iconSize={17} />)}
          </div>
        </div>
      </Widget>
    </SectionShell>
  );
}

/* ── Mockup: Bonus & Rewards panel ── */
function BonusRewards({ data = {}, trackAndGo }) {
  const list = panelItems(data, BONUS_ITEMS);
  return (
    <SectionShell tint className="mk-panel">
      <Widget title={data.heading || "Bonus & Rewards"} testid="bonus-rewards" action={<span className="text-slate-400 text-xs hidden sm:inline">{data.subheading || "More trading. More rewards."}</span>}>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#1E2740]">
          {list.map((it, i) => <PanelItem key={i} it={it} testid="reward" trackAndGo={trackAndGo} />)}
        </div>
      </Widget>
    </SectionShell>
  );
}

/* ── Mockup: Mobile Apps panel ── */
function MobileAppsPanel({ data = {}, trackAndGo }) {
  return (
    <section className="mk-panel bg-[#05070D] border-t border-[#1E2740] py-14" data-testid="mobile-apps">
      <div className="cb-container grid lg:grid-cols-2 gap-10 items-center">
        <div className="relative flex justify-center">
          <div className="absolute inset-0 gold-radial" />
          <img src="/images/dark_mobile_app.jpg" alt="Cresent Bank mobile app" className="relative w-full max-w-[280px] object-contain drop-shadow-[0_24px_48px_rgba(216,174,78,0.25)]" loading="lazy" />
        </div>
        <div>
          <p className="overline mb-2">Mobile Apps</p>
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-white tracking-tight">{data.heading || "Trade on the go, anytime, anywhere"}</h2>
          <p className="text-slate-400 mt-4 leading-relaxed max-w-md">{data.subheading || "Access live markets, banking and your portfolio from one secure app. Available on iOS and Android."}</p>
          <div className="flex flex-wrap gap-3 mt-7">
            <button onClick={() => trackAndGo("ios_download")} className="btn-outline-gold px-5 py-3 inline-flex items-center gap-2 text-sm" data-testid="app-ios"><Monitor size={16} /> App Store</button>
            <button onClick={() => trackAndGo("android_download")} className="btn-outline-gold px-5 py-3 inline-flex items-center gap-2 text-sm" data-testid="app-android"><Monitor size={16} /> Google Play</button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Mockup: shared icon-list dark panel (CMS heading/subheading + items via data.config.items) ── */
const AUTOMATED_ITEMS = [
  { icon: "bot", title: "Strategy Marketplace", desc: "Ready-made & custom EAs", link: "mt5_download" },
  { icon: "zap", title: "AI Trading Tools", desc: "Smarter decisions, better results", link: "/platforms" },
  { icon: "layers", title: "Copy Trading", desc: "Follow expert traders", link: "client_portal" },
];
const WEALTH_ITEMS = [
  { icon: "landmark", title: "Managed Accounts", desc: "Professional management", link: "client_portal" },
  { icon: "trending-up", title: "Income Programs", desc: "Earn passive income", link: "client_portal" },
  { icon: "shield", title: "Self-Directed Investing", desc: "Grow your portfolio", link: "client_portal" },
  { icon: "bitcoin", title: "Staking & Crypto Programs", desc: "Grow your digital assets", link: "wallet" },
];
const INSTITUTIONAL_ITEMS = [
  { icon: "layers", title: "Liquidity Solutions", desc: "Deep multi-venue liquidity", link: "/conditions" },
  { icon: "landmark", title: "Brokerage Services", desc: "White-label & IB", link: "ib_portal" },
  { icon: "monitor", title: "Technology & API", desc: "FIX & REST connectivity", link: "/platforms" },
  { icon: "globe", title: "Professional Services", desc: "Dedicated onboarding", link: "/contact" },
];
const SECURITY_ITEMS = [
  { icon: "shield", title: "Regulated Services", desc: "Licensed & compliant", link: "/conditions" },
  { icon: "shield", title: "Audit & Transparency", desc: "Independently audited", link: "/conditions" },
  { icon: "wallet", title: "Segregated Funds", desc: "Held with tier-1 banks", link: "/conditions" },
  { icon: "lock", title: "Advanced Encryption", desc: "Bank-grade security", link: "/conditions" },
];
const BANKING_ITEMS = [
  { icon: "landmark", title: "Multi-Currency Accounts", desc: "USD, EUR, GBP, CHF & more", link: "client_portal" },
  { icon: "card", title: "IBAN Accounts", desc: "Receive & send globally", link: "client_portal" },
  { icon: "monitor", title: "Video Account Opening", desc: "Fast & secure", link: "client_portal" },
  { icon: "card", title: "Virtual & Physical Cards", desc: "Worldwide acceptance", link: "payment_gateway" },
  { icon: "wallet", title: "Payments & Transfers", desc: "Local & international", link: "deposit" },
  { icon: "coins", title: "Currency Conversion", desc: "Competitive rates", link: "client_portal" },
  { icon: "landmark", title: "Business Accounts", desc: "For companies & institutions", link: "client_portal" },
  { icon: "globe", title: "Wealth & Private Banking", desc: "Personalised solutions", link: "client_portal" },
];
const BONUS_ITEMS = [
  { icon: "coins", title: "Cashback Program", desc: "Trade and get rewarded", link: "live_register" },
  { icon: "star", title: "Equity Loyalty Program", desc: "Exclusive benefits", link: "live_register" },
  { icon: "user-plus", title: "Referral Program", desc: "Invite & earn", link: "live_register" },
  { icon: "zap", title: "Trading Contests", desc: "Compete for prizes", link: "live_register" },
];
function panelItems(data, fallback) {
  return (data && data.config && Array.isArray(data.config.items) && data.config.items.length) ? data.config.items : fallback;
}
function PanelItem({ it, testid, trackAndGo, iconSize = 18 }) {
  const Icon = PANEL_ICONS[it.icon] || Layers;
  const title = it.title || it.t || "";
  const desc = it.desc || it.d || "";
  const link = it.link || it.to || it.key || "";
  const internal = typeof link === "string" && link.startsWith("/");
  const inner = (<><span className="w-10 h-10 rounded border border-[#1E2740] bg-[#05070D] flex items-center justify-center shrink-0"><Icon size={iconSize} className="text-[#D8AE4E]" /></span><span><span className="block text-sm text-white font-medium">{title}</span>{desc && <span className="block text-xs text-slate-500 mt-0.5">{desc}</span>}</span></>);
  const cls = "flex items-start gap-3 p-5 bg-[#0B0F1A] hover:bg-[#0E1424] transition-colors text-left w-full";
  const tid = `${testid}-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}`;
  return internal
    ? <Link to={link} className={cls} data-testid={tid}>{inner}</Link>
    : <button onClick={() => trackAndGo(link)} className={cls} data-testid={tid}>{inner}</button>;
}
function IconPanel({ data = {}, title, sub, testid, tint, items, trackAndGo, cols = "lg:grid-cols-3" }) {
  const list = panelItems(data, items);
  return (
    <SectionShell tint={tint} className="mk-panel">
      <Widget title={data.heading || title} testid={testid} action={<span className="text-slate-400 text-xs hidden sm:inline">{data.subheading || sub}</span>}>
        <div className={`grid sm:grid-cols-2 ${cols} gap-px bg-[#1E2740]`}>
          {list.map((it, i) => <PanelItem key={i} it={it} testid={testid} trackAndGo={trackAndGo} />)}
        </div>
      </Widget>
    </SectionShell>
  );
}

/* ── Mockup: Market Calendar compact panel ── */
function MarketCalendarPanel({ data = {}, events }) {
  const up = [...(events || [])].sort((a, b) => new Date(a.date) - new Date(b.date)).slice(0, 5);
  return (
    <SectionShell tint className="mk-panel">
      <Widget title={data.heading || "Market Calendar"} testid="market-calendar-panel" action={<Link to="/economic-calendar" className="text-[#D8AE4E] text-xs hover:underline">Full calendar ›</Link>}>
        <div className="divide-y divide-slate-100 bg-[#0B0F1A]">
          {up.length ? up.map((e) => (
            <div key={e.id} className="flex items-center gap-3 px-4 py-2.5 text-sm" data-testid={`mkt-cal-${e.id}`}>
              <span className="font-mono text-[11px] text-slate-400 w-14 shrink-0">{e.date ? new Date(e.date).toLocaleDateString(undefined, { day: "2-digit", month: "short" }) : "—"}</span>
              <span className="font-mono text-[#D8AE4E] w-10 shrink-0">{e.currency}</span>
              <span className="text-white flex-1 min-w-0 truncate">{e.title}</span>
              <ImpactBadge impact={e.impact} />
            </div>
          )) : <p className="px-4 py-6 text-sm text-slate-500">No upcoming events.</p>}
        </div>
      </Widget>
    </SectionShell>
  );
}

/* ── Mockup: section imagery + additional item lists ── */
const SEC_IMG = {
  automated: "/images/dark_auto_ai.jpg",
  wealth: "/images/sec_wealth.jpg",
  research_tools: "/images/sec_research.jpg",
  rewards: "/images/sec_bonus.jpg",
  funding: "/images/sec_funding.jpg",
  institutional: "/images/sec_institutional.jpg",
  security: "/images/sec_security.jpg",
  support: "/images/sec_support.jpg",
};
const TRADING_ITEMS = [
  { icon: "globe", title: "Forex", desc: "Majors, Minors, Exotics", link: "/markets" },
  { icon: "chart", title: "Stocks", desc: "Global exchanges", link: "/markets" },
  { icon: "bitcoin", title: "Crypto", desc: "Leading digital assets", link: "/markets" },
  { icon: "coins", title: "Commodities", desc: "Gold, Silver, Oil, Gas", link: "/markets" },
  { icon: "trending-up", title: "Indices", desc: "Global indices", link: "/markets" },
  { icon: "landmark", title: "Bonds", desc: "Government & Corporate", link: "/markets" },
  { icon: "layers", title: "ETFs", desc: "Diversified exposure", link: "/markets" },
  { icon: "monitor", title: "Account Types", desc: "Standard, ECN, VIP, Islamic", link: "/accounts" },
];
const RESEARCH_ITEMS = [
  { icon: "zap", title: "Trading Ideas", desc: "Actionable setups", link: "/trading-ideas" },
  { icon: "calendar", title: "Market News", desc: "Latest headlines", link: "/news" },
  { icon: "calendar", title: "Economic Calendar", desc: "Key events", link: "/economic-calendar" },
  { icon: "chart", title: "Sentiment Index", desc: "Market positioning", link: "/education" },
  { icon: "chart", title: "Market Analysis", desc: "Expert insight", link: "/education" },
  { icon: "monitor", title: "Technical Analysis", desc: "Charts & indicators", link: "/education" },
  { icon: "coins", title: "Forex Calculator", desc: "Position & margin", link: "/education" },
  { icon: "layers", title: "Historical Data", desc: "Export & backtest", link: "/education" },
];
const FUNDING_ITEMS = [
  { icon: "landmark", title: "Bank Transfers", desc: "Wire & local", link: "deposit" },
  { icon: "card", title: "Credit / Debit Cards", desc: "Instant deposits", link: "deposit" },
  { icon: "wallet", title: "E-Wallets", desc: "Skrill, Neteller", link: "deposit" },
  { icon: "globe", title: "Local Payments", desc: "Regional methods", link: "deposit" },
  { icon: "bitcoin", title: "Cryptocurrency", desc: "BTC, ETH, USDT", link: "deposit" },
  { icon: "coins", title: "Global Wire Transfers", desc: "Worldwide reach", link: "deposit" },
];
const SUPPORT_ITEMS = [
  { icon: "support", title: "24/7 Customer Support", desc: "Always available", link: "support" },
  { icon: "globe", title: "Multi-Language Support", desc: "In your language", link: "support" },
  { icon: "layers", title: "Knowledge Base", desc: "Guides & FAQs", link: "/faq" },
  { icon: "monitor", title: "Video Tutorials", desc: "Learn by watching", link: "/education" },
  { icon: "star", title: "Webinars & Education", desc: "Live sessions", link: "/education" },
  { icon: "user-plus", title: "Community Forum", desc: "Connect with traders", link: "support" },
];

function FeaturePanel({ data = {}, title, sub, testid, image, cta, ctaKey, defaults, trackAndGo, cols = "sm:grid-cols-2", tint }) {
  const list = panelItems(data, defaults);
  return (
    <SectionShell tint={tint} className="mk-panel">
      <div className="mb-5" data-testid={`${testid}-head`}>
        <h2 className="font-serif text-2xl sm:text-3xl font-bold text-white tracking-tight">{data.heading || title}</h2>
        <p className="text-slate-400 text-sm mt-1">{data.subheading || sub}</p>
      </div>
      <div className="grid lg:grid-cols-12 gap-0 border border-[#1E2740] rounded overflow-hidden" data-testid={testid}>
        <div className="lg:col-span-4 relative min-h-[240px] overflow-hidden">
          <img src={image} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#05070D] via-[#05070D]/40 to-transparent" />
          {cta && (String(ctaKey).startsWith("/")
            ? <Link to={ctaKey} className="btn-gold px-4 py-2 text-xs absolute bottom-4 left-4" data-testid={`${testid}-cta`}>{cta}</Link>
            : ctaKey ? <button onClick={() => trackAndGo(ctaKey)} className="btn-gold px-4 py-2 text-xs absolute bottom-4 left-4" data-testid={`${testid}-cta`}>{cta}</button> : null)}
        </div>
        <div className={`lg:col-span-8 grid ${cols} gap-px bg-[#1E2740]`}>
          {list.map((it, i) => <PanelItem key={i} it={it} testid={testid.replace("-panel", "")} trackAndGo={trackAndGo} />)}
        </div>
      </div>
    </SectionShell>
  );
}
